import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { UserRole, isAdminUser } from '../types/auth';
import IndividualProfileView from './IndividualProfileView';
import InstitutionProfile from './InstitutionProfile';
import { Container, Stack, Flex } from '../design-system/components/Layout';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Avatar } from '../components/common/Avatar';
import { spacing } from '../design-system/tokens/spacing';

const MinimalProfileContainer = styled(Container)`
  max-width: 600px;
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[8]};
`;

/**
 * Role-based profile router. Delegates to the appropriate profile view
 * based on the authenticated user's role.
 */
const Profile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!user) {
    return null;
  }

  if (user.role === UserRole.InstitutionMember) {
    return <InstitutionProfile />;
  }

  if (user.role === UserRole.IndividualMember) {
    return <IndividualProfileView />;
  }

  // Administrator: minimal profile (no member record)
  if (isAdminUser(user)) {
    return (
      <MinimalProfileContainer>
        <Stack direction="column" spacing="xl">
          <CardWithPadding variant="elevated" accentBorder="top" pattern="organic" patternIntensity="subtle">
            <Stack direction="column" spacing="lg" style={{ position: 'relative', zIndex: 1 }}>
              <Flex direction="row" align="center" gap="lg">
                <Avatar name={`${user.firstName} ${user.lastName}`} size="xl" border borderColor="primary" />
                <Stack direction="column" spacing="sm">
                  <Heading variant="h3" color="text">
                    {user.firstName} {user.lastName}
                  </Heading>
                  <Text size="sm" color="secondary">
                    {user.email}
                  </Text>
                </Stack>
              </Flex>
              <Button variant="outline" onClick={() => navigate('/admin')}>
                {t('nav.dashboard')}
              </Button>
            </Stack>
          </CardWithPadding>
        </Stack>
      </MinimalProfileContainer>
    );
  }

  return <IndividualProfileView />;
};

export default Profile;
